import type { Middleware, Store } from '@reduxjs/toolkit';

import { WATERMARK_STORAGE_KEY } from '../hooks/useOrgWatermarkAvailable';
import {
  entityRenamed,
  initialEntityScopeState,
  entityTreeLoaded,
  rootMovedSince,
  scopeChanged,
  scopeCleared,
  scopeHydrated,
  scopeRootStamped,
  userChangedSince,
  type EntityScopeState,
} from '../slice/entityScopeSlice';
import type { EntityTreeNode } from '../types/order.types';
import { scopeRootOfStoredToken, subjectOfStoredToken } from '../util/jwt';

/**
 * Web Storage as the entity-scope slice's serialization format (DV-3013 spec
 * §3.2 / D4), per TAB since DV-3573.
 *
 * <p><b>Two stores.</b> `sessionStorage` holds THIS tab's scope, so two tabs
 * can sit in different accounts. `localStorage` holds the SEED: the account
 * last chosen in this browser, which a brand-new tab starts in and then
 * diverges from. A tab reads its own store once it has one (marked by
 * {@link TAB_SCOPE_MARKER}), and the seed until then.
 *
 * <p><b>Nine keys</b> in each: selectedOrg, selectedEntity, parentEntityId,
 * hasWatermark, skipCheckout, entityTree — which keep the exact names and
 * encodings they had before the slice existed, so a session already logged in
 * when this deploys keeps working (its localStorage keys become its seed) —
 * plus DV-3573's scopeSub (the user the scope was written under) and scopeRole
 * (the realm role in that account), and DV-3421's scopeRoot (the token's scope
 * root it was written under). A session persisted before those has none of the
 * three and reads them as null; see `userChangedSince` and `getScopeRole`.
 * `e2e/support/seedAuth.ts` plants the first eight as the seed.
 *
 * <p>`currentContext` and `role` are NOT among them. Both are written by login
 * and by `switchEntity` for readers that have not moved to the slice; the
 * slice is the source of truth (`getCurrentContext`, `getScopeRole`).
 *
 * <p><b>The seed means "the account last chosen", and only a choice writes
 * it</b> — see `scopePersistenceMiddleware`. A tab persisting whatever it
 * holds would make the seed last-writer-wins: reloading an old tab, or a tab
 * still on Y loading its entity tree, would re-point every future new tab.
 */

const parseTree = (raw: string | null): EntityTreeNode | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    // A corrupt payload must not throw on the pre-render bootstrap path.
    return parsed && typeof parsed === 'object' ? (parsed as EntityTreeNode) : null;
  } catch {
    return null;
  }
};

const parseWatermark = (raw: string | null): boolean | null => {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  // Absent, the JSON string 'null', or anything unexpected: no signal.
  return null;
};

/**
 * Marks a tab as owning its own scope. Written whenever the tab's store is;
 * read to decide which store a read comes from.
 *
 * <p>A bare "does sessionStorage have selectedEntity" test will not do — an
 * UNSCOPED tab legitimately has none, and falling back to `localStorage` there
 * would resurrect an account the user just un-chose. This marker separates
 * "this tab has no scope" from "this tab has not been seeded yet".
 *
 * <p>Exported for fixtures: a test that seeds `localStorage` to represent "the
 * last account used in this browser" is describing a tab that has NOT claimed
 * its own scope yet, and must clear this for that to be true.
 */
export const TAB_SCOPE_MARKER = 'scopeTab';

/**
 * The store this tab's scope is read from.
 *
 * <p>A tab that has claimed its scope reads its own. A brand-new tab has none
 * and inherits the seed — so opening a second tab lands where you left off
 * rather than on the account picker, which for the ~98.9% of customers with a
 * single entity would be a click they get nothing for.
 *
 * <p>Chrome copies `sessionStorage` into a duplicated tab, so Duplicate Tab
 * starts in the same account and diverges from there, for free.
 */
const scopeStore = (): Storage =>
  sessionStorage.getItem(TAB_SCOPE_MARKER) !== null ? sessionStorage : localStorage;

/**
 * Every key {@link writeScope} owns. Listed only for the one case that has to
 * remove a scope without writing a replacement over it.
 */
const SCOPE_KEYS = [
  'selectedOrg',
  'selectedEntity',
  'parentEntityId',
  WATERMARK_STORAGE_KEY,
  'skipCheckout',
  'entityTree',
  'scopeSub',
  'scopeRoot',
  'scopeRole',
];

/**
 * Throw away this tab's own persisted scope, and the shared seed too when the
 * seed belongs to a DIFFERENT USER.
 *
 * <p>DV-3573 review round 2. `endSession()` clears `localStorage` and the
 * `sessionStorage` of the tab it runs in — it cannot reach another tab's. So
 * tab B's `sessionStorage` can outlive a logout in tab A, still holding A's
 * account, and a different user later booting in tab B would land inside it.
 * Fixed at the READ, because a read that refuses to return another user's
 * scope cannot be bypassed by a login path added later.
 *
 * <p>A seed for the SAME user under an older root is not removed, only not
 * used (see {@link readPersistedScope}). It is shared: the tab that switched
 * writes the new root into it a moment after the new token lands, and a tab
 * that reloads on the token write can read it in between (review round 4,
 * issue 6). Deleting it then would wipe the seed the switching tab is about
 * to rewrite — and if that tab's write had already happened, a good one.
 */
const discardForeignScope = (sub: string): void => {
  sessionStorage.removeItem(TAB_SCOPE_MARKER);
  SCOPE_KEYS.forEach((key) => sessionStorage.removeItem(key));

  const seedSub = localStorage.getItem('scopeSub');
  if (seedSub !== null && seedSub !== sub) {
    SCOPE_KEYS.forEach((key) => localStorage.removeItem(key));
  }
};

/**
 * Was `scope` written under a different user, or under a different scope root,
 * than the token now holds?
 *
 * <p>Both sides of each pair have to be known before a mismatch means
 * anything, hence the null tests: an absent stamp is a session persisted
 * before the stamp existed, and an absent token value is a tab with nobody
 * logged in. Discarding on either would throw away scope from valid sessions.
 */
const writtenUnderAnotherToken = (
  scope: EntityScopeState,
  sub: string,
  root: string | null,
): { differentUser: boolean; differentRoot: boolean } => ({
  differentUser: userChangedSince(sub, scope),
  // DV-3421: the token's ROOT moves when a cross-tree switch re-anchors it, in
  // ANY tab, since the token is shared. A scope written under the old root
  // cannot be reached and would be refused on every request. This is what
  // makes `reconcileScope`'s reload converge.
  differentRoot: rootMovedSince(root, scope),
});

/** The raw scope held by one store, with no staleness judgement. */
const readFrom = (store: Storage): EntityScopeState => {
  const entityName = store.getItem('selectedOrg');
  return {
    // `selectedOrg`'s presence is what "an entity was chosen in this tab" has
    // always meant — see the slice's `status` docstring.
    status: entityName ? 'scoped' : 'unscoped',
    entityId: store.getItem('selectedEntity'),
    entityName,
    parentEntityId: store.getItem('parentEntityId'),
    // DV-3573. Absent for a session persisted before this shipped. Null is
    // not a mismatch anywhere it is read: the header reader suppresses
    // nothing, and the check below discards nothing.
    subAtWrite: store.getItem('scopeSub'),
    rootAtWrite: store.getItem('scopeRoot'),
    role: store.getItem('scopeRole'),
    hasWatermark: parseWatermark(store.getItem(WATERMARK_STORAGE_KEY)),
    skipCheckout: store.getItem('skipCheckout') === 'true',
    entityTree: parseTree(store.getItem('entityTree')),
  };
};

/**
 * This tab's persisted scope: its own store, else the seed — never a scope
 * written under a different user, or a different token scope root, than the
 * token holds now.
 *
 * <p>Since the DV-3573 review the bootstrap does not hydrate without a token
 * at all, so a read with no token subject has nothing to protect and returns
 * the scope as stored.
 */
export const readPersistedScope = (): EntityScopeState => {
  const scope = readFrom(scopeStore());

  const sub = subjectOfStoredToken();
  if (sub === null) return scope;
  const root = scopeRootOfStoredToken();

  const own = writtenUnderAnotherToken(scope, sub, root);
  if (!own.differentUser && !own.differentRoot) return scope;

  discardForeignScope(sub);
  // Read ONCE more, and judge what comes back by the same test — never
  // recurse (review round 2, issue 2: a seed the discard legitimately kept
  // failed the same check forever and crashed the page at load). The discard
  // dropped this tab's marker, so this reads the shared seed: a tab whose own
  // copy was stale still lands on the account this browser last used, when
  // that is valid under the current token. When it is not, the tab starts
  // unscoped and the seed is left for its owner to rewrite.
  const seed = readFrom(scopeStore());
  const seedCheck = writtenUnderAnotherToken(seed, sub, root);
  return seedCheck.differentUser || seedCheck.differentRoot ? initialEntityScopeState : seed;
};


const writeScope = (target: Storage, state: EntityScopeState): void => {
  const setOrRemove = (key: string, value: string | null): void => {
    if (value === null) target.removeItem(key);
    else target.setItem(key, value);
  };
  const chosenNothing = state.status === 'unscoped' && state.entityId === null;

  setOrRemove('selectedOrg', state.entityName);
  setOrRemove('selectedEntity', state.entityId);
  setOrRemove('parentEntityId', state.parentEntityId);
  // Deliberately NOT removed when null: switchEntity has always written
  // JSON.stringify(null) here, and keeping that encoding keeps the stored
  // shape of an existing session unchanged.
  setOrRemove(WATERMARK_STORAGE_KEY, chosenNothing ? null : JSON.stringify(state.hasWatermark));
  setOrRemove('skipCheckout', chosenNothing ? null : JSON.stringify(state.skipCheckout));
  setOrRemove('entityTree', state.entityTree ? JSON.stringify(state.entityTree) : null);
  setOrRemove('scopeSub', state.subAtWrite);
  setOrRemove('scopeRoot', state.rootAtWrite);
  setOrRemove('scopeRole', state.role);
};

/**
 * This tab's own scope. Claims the tab first, so every later read comes from
 * `sessionStorage` — including one that interleaves with this write: a
 * half-written tab store is recoverable, silently reading the seed instead is
 * not.
 */
const writeTabScope = (state: EntityScopeState): void => {
  sessionStorage.setItem(TAB_SCOPE_MARKER, '1');
  writeScope(sessionStorage, state);
};

/** The account last chosen in this browser, for the next new tab. */
const writeSeed = (state: EntityScopeState): void => writeScope(localStorage, state);

/** A choice: this tab's scope AND the seed. */
export const writePersistedScope = (state: EntityScopeState): void => {
  writeTabScope(state);
  writeSeed(state);
};

/**
 * Adopt what was persisted. Called explicitly at bootstrap rather than read at
 * module evaluation: an import-time read would re-enter the ordering hazard
 * get-store.ts exists to close, and would make every test that touches the
 * store order-dependent (spec D2).
 */
export const hydrateEntityScope = (store: Store): void => {
  store.dispatch(scopeHydrated(readPersistedScope()));
};

/**
 * The ONLY writer of the scope keys, in both stores.
 *
 * <p>A middleware rather than a store subscriber because WHICH action moved
 * the slice decides what to write, and a subscriber cannot see it:
 *
 * <ul>
 *   <li><b>`scopeChanged` / `scopeCleared`</b> — this tab chose or un-chose an
 *       account. Its own store, and the seed: that is what "the account last
 *       chosen" means.
 *   <li><b>`entityRenamed` / `entityTreeLoaded`</b> — detail about the account
 *       this tab holds, so only while it holds one: a rename landing after
 *       "Switch Organization" would otherwise write `selectedOrg` back and
 *       read as scoped. Its own store; the seed only when the seed names that
 *       same account, so a tab still on Y cannot re-point it after another tab
 *       chose X.
 *   <li><b>`scopeHydrated`</b> — a READ. Never touches the seed: reloading an
 *       old tab must not re-point every future new tab at its account, and a
 *       read that came back empty must not wipe a seed it did not write. A
 *       scoped result claims the tab, so a tab that started from the seed
 *       keeps its account across reloads even after the seed moves on; an
 *       unscoped one claims nothing, so the next reload still looks at the
 *       seed.
 *   <li><b>`scopeRootStamped`</b> — a missing root stamp filled in after the
 *       backend answered for it (DV-3421). Its own store; the seed only when
 *       the seed is that same account, still unstamped.
 * </ul>
 */
export const scopePersistenceMiddleware: Middleware = (api) => (next) => (action) => {
  const result = next(action);
  const state = (api.getState() as { entityScope: EntityScopeState }).entityScope;

  if (scopeChanged.match(action) || scopeCleared.match(action)) {
    writePersistedScope(state);
  } else if ((entityRenamed.match(action) || entityTreeLoaded.match(action)) && state.status === 'scoped') {
    writeTabScope(state);
    if (state.entityId !== null && localStorage.getItem('selectedEntity') === state.entityId) {
      writeSeed(state);
    }
  } else if (scopeHydrated.match(action) && state.status === 'scoped') {
    writeTabScope(state);
  } else if (scopeRootStamped.match(action) && state.status === 'scoped') {
    // DV-3421 round 6, item A. The seed too, when it is the same unstamped
    // scope: otherwise a tab stamped UNREACHABLE reloads, discards its own
    // copy, and adopts the seed's identical unstamped one — and checks again,
    // forever. A seed another tab has stamped is left alone.
    writeTabScope(state);
    if (
      state.entityId !== null &&
      localStorage.getItem('selectedEntity') === state.entityId &&
      localStorage.getItem('scopeRoot') === null
    ) {
      writeSeed(state);
    }
  }
  return result;
};

/**
 * The scope root the shared seed was written under, or null (DV-3421 review
 * round 5, item 5). A tab that just switched across trees writes the seed a
 * moment AFTER its new token lands; `reconcileScope` waits for the two to agree
 * before reloading another tab, so that reload adopts the new account instead
 * of finding a seed it must discard.
 */
export const seedScopeRoot = (): string | null => localStorage.getItem('scopeRoot');
